import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { disabled, form, FormField, min } from '@angular/forms/signals';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AddPricingCountryBundlePriceAction } from '../../../store/pricing-country.actions';
import {
  CountryBundlePrice,
  CountryBundleRow,
  CountryPriceType,
} from '../../../types/pricing.types';

interface CountryPricesModel {
  wsp: number | null;
  eup: number | null;
}

@Component({
  selector: 'app-edit-country-price-dialog',
  imports: [FormField, TranslatePipe, DatePipe],
  templateUrl: './edit-country-price-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './edit-country-price-dialog.component.scss',
})
export class EditCountryPriceDialogComponent {
  public countryId = signal<string>(null);
  public row = signal<CountryBundleRow>(null);
  public currency = signal<string>(null);

  protected modalRef = inject(NgbActiveModal);
  private store = inject(Store);

  protected readonly CountryPriceType = CountryPriceType;

  protected pricesModel = signal<CountryPricesModel>({ wsp: null, eup: null });
  protected pricesForm = form(this.pricesModel, (path) => {
    min(path.wsp, 1);
    min(path.eup, 1);
    disabled(path.wsp, () => !this.isEditable(CountryPriceType.WSP));
    disabled(path.eup, () => !this.isEditable(CountryPriceType.EUP));
  });

  protected hasChanges = computed(
    () =>
      this.isChanged(CountryPriceType.WSP) ||
      this.isChanged(CountryPriceType.EUP)
  );

  protected submitDisabled = computed(
    () => this.pricesForm().invalid() || !this.hasChanges()
  );

  constructor() {
    effect(() => {
      const row = this.row();

      if (row) {
        this.pricesModel.set({
          wsp: row.wsp?.pivot?.price ?? null,
          eup: row.eup?.pivot?.price ?? null,
        });
      }
    });
  }

  protected priceOf(type: CountryPriceType): CountryBundlePrice | null {
    return type === CountryPriceType.WSP ? this.row()?.wsp : this.row()?.eup;
  }

  /** Only a missing price can be set here (same rule as for partners). */
  protected isEditable(type: CountryPriceType): boolean {
    return !this.priceOf(type)?.pivot?.price;
  }

  protected save() {
    if (this.submitDisabled()) {
      return;
    }

    const actions = [CountryPriceType.WSP, CountryPriceType.EUP]
      .filter((type) => this.isChanged(type))
      .map(
        (type) =>
          new AddPricingCountryBundlePriceAction(
            this.countryId(),
            this.row().id,
            Number(this.valueOf(type)),
            type,
            // first price: null, the backend applies 1 January 1970
            null
          )
      );

    this.store.dispatch(actions).subscribe(() => this.modalRef.close());
  }

  private valueOf(type: CountryPriceType): number | null {
    return type === CountryPriceType.WSP
      ? this.pricesModel().wsp
      : this.pricesModel().eup;
  }

  private isChanged(type: CountryPriceType): boolean {
    const value = this.valueOf(type);

    if (!this.isEditable(type) || value === null || value === undefined) {
      return false;
    }

    return Number(value) !== (this.priceOf(type)?.pivot?.price ?? null);
  }
}
